import { Card } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { usePendientes } from '@/hooks/useReportes';
import { cn } from '@/lib/utils';
import type { PendienteItem } from '@adelante/shared';
import { CircleNotch } from '@phosphor-icons/react';
import { useMemo, useState } from 'react';

type Agrupar = 'obra' | 'sub';

/** % de avance coloreado (0 rojo suave, parcial ámbar). */
function colorPct(v: number): string {
  if (v <= 0) return 'text-red-600';
  return 'text-amber-600';
}

export function PendientesVista() {
  const { data, isLoading, isError } = usePendientes();
  const items = useMemo(() => data?.data?.items ?? [], [data]);
  const [agrupar, setAgrupar] = useState<Agrupar>('sub');
  const [proyecto, setProyecto] = useState<string>('TODOS');

  const proyectos = useMemo(() => [...new Set(items.map((i) => i.proyecto))].sort(), [items]);
  const filtrados = useMemo(
    () => (proyecto === 'TODOS' ? items : items.filter((i) => i.proyecto === proyecto)),
    [items, proyecto],
  );

  const kpis = useMemo(() => {
    const obras = new Set(filtrados.map((i) => i.obra));
    return {
      pendientes: filtrados.length,
      obras: obras.size,
      conNC: filtrados.filter((i) => i.nc_causa).length,
      sinAvance: filtrados.filter((i) => i.pct <= 0).length,
      tresSem: filtrados.filter((i) => i.sem_arrastrada >= 3).length,
    };
  }, [filtrados]);

  // Agrupar por sub-partida (nombre) o por obra.
  const grupos = useMemo(() => {
    const m = new Map<string, PendienteItem[]>();
    for (const i of filtrados) {
      const clave = agrupar === 'sub' ? i.sub_nombre : i.obra;
      const arr = m.get(clave) ?? [];
      arr.push(i);
      m.set(clave, arr);
    }
    return [...m.entries()].sort(([a], [b]) => a.localeCompare(b, 'es', { numeric: true }));
  }, [filtrados, agrupar]);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-12 text-sm text-muted-foreground">
        <CircleNotch className="h-4 w-4 animate-spin" weight="bold" aria-hidden="true" />
        Cargando pendientes…
      </div>
    );
  }
  if (isError) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        No se pudieron cargar los pendientes.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-2">
        <Select value={proyecto} onValueChange={setProyecto}>
          <SelectTrigger className="w-[200px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="TODOS">Todos los proyectos</SelectItem>
            {proyectos.map((p) => (
              <SelectItem key={p} value={p}>
                {p}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="inline-flex rounded-md border border-border p-0.5">
          {(
            [
              ['obra', 'Por Obra'],
              ['sub', 'Por Sub-partida'],
            ] as [Agrupar, string][]
          ).map(([v, label]) => (
            <button
              key={v}
              type="button"
              onClick={() => setAgrupar(v)}
              className={cn(
                'rounded px-3 py-1 text-xs font-medium transition-colors',
                agrupar === v
                  ? 'bg-adelante-primary text-adelante-foreground'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <Kpi label="Pendientes" value={kpis.pendientes} />
        <Kpi label="Obras afectadas" value={kpis.obras} accent="blue" />
        <Kpi label="Con NC" value={kpis.conNC} />
        <Kpi
          label="Sin avance"
          value={kpis.sinAvance}
          accent={kpis.sinAvance > 0 ? 'red' : undefined}
        />
        <Kpi label="≥3 sem." value={kpis.tresSem} accent={kpis.tresSem > 0 ? 'red' : undefined} />
      </div>

      {grupos.length === 0 && (
        <p className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
          No hay pendientes arrastrados. 🎉
        </p>
      )}

      {grupos.map(([clave, lista]) => (
        <Card key={clave} className="overflow-hidden">
          <div className="flex items-center justify-between border-b border-border bg-zinc-50 px-3 py-1.5">
            <span className="text-sm font-semibold">
              {agrupar === 'sub' && lista[0]?.es_critica && (
                <span className="mr-1 text-red-500">●</span>
              )}
              {clave}
            </span>
            <span className="text-xs text-muted-foreground">{lista.length} obras</span>
          </div>
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-border text-[11px] uppercase tracking-wide text-muted-foreground">
                <th className="px-3 py-1.5 text-left">
                  {agrupar === 'sub' ? 'Obra' : 'Sub-partida'}
                </th>
                <th className="px-3 py-1.5 text-left">Tipo</th>
                <th className="px-3 py-1.5 text-left">Proyecto</th>
                <th className="px-3 py-1.5 text-left">Sprint origen</th>
                <th className="px-3 py-1.5 text-right">% Avance</th>
                <th className="px-3 py-1.5 text-left">NC</th>
                <th className="px-3 py-1.5 text-right">Sem.</th>
              </tr>
            </thead>
            <tbody>
              {lista.map((i) => (
                <tr key={`${i.obra}|${i.sub_id}`} className="border-b border-border last:border-0">
                  <td className="px-3 py-1.5 font-mono text-xs font-semibold">
                    {agrupar === 'sub' ? i.obra : i.sub_nombre}
                  </td>
                  <td className="px-3 py-1.5 text-xs text-muted-foreground">
                    {i.tipo_casa ?? '—'}
                  </td>
                  <td className="px-3 py-1.5 text-xs text-muted-foreground">{i.proyecto}</td>
                  <td className="px-3 py-1.5 text-xs text-muted-foreground">
                    S{i.sprint_origen}
                    {i.sprint_origen_nombre ? ` · ${i.sprint_origen_nombre}` : ''}
                  </td>
                  <td
                    className={cn(
                      'px-3 py-1.5 text-right font-mono tabular-nums font-semibold',
                      colorPct(i.pct),
                    )}
                  >
                    {Math.round(i.pct)}%
                  </td>
                  <td className="px-3 py-1.5 text-xs">
                    {i.nc_causa ? (
                      <span className="rounded bg-red-100 px-1 text-[10px] font-semibold uppercase text-red-700">
                        {i.nc_causa}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{i.sem_arrastrada}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      ))}
    </div>
  );
}

function Kpi({
  label,
  value,
  accent,
}: {
  label: string;
  value: number;
  accent?: 'red' | 'blue';
}) {
  return (
    <Card className="p-3">
      <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p
        className={cn(
          'text-2xl font-semibold tabular-nums',
          accent === 'red' && 'text-red-600',
          accent === 'blue' && 'text-blue-600',
        )}
      >
        {value}
      </p>
    </Card>
  );
}
